import { onClickOutside, onKeyStroke, useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
import { toBoolean } from "#ce/utils/props";
import { useDialogFocus } from "./useDialogFocus.js";
import { useDialogScrollLock } from "./useDialogScrollLock.js";
import { State, useDialogState } from "./useDialogState.js";
import { useDialogTransition } from "./useDialogTransition.js";

/** @typedef {import("../props.js").SpDialogProps} SpDialogProps */

// The triggers handlers report to `dispatch`. The engine maps each to a
// lifecycle action from the current state, so a handler never chooses open vs
// close itself.
const Trigger = {
  OPEN: "open",
  CLOSE: "close",
  REVEAL: "reveal",
  DISMISS: "dismiss",
};

/**
 * Orchestrates the sp-dialog open/close lifecycle by composing the state machine
 * (`useDialogState`), interruptible transition settling (`useDialogTransition`),
 * focus (`useDialogFocus`), and the scroll lock (`useDialogScrollLock`). It owns
 * the lifecycle actions, the trigger dispatch engine, and the prop/event wiring.
 * The host component supplies the `<dialog>` and content template refs and stays
 * a thin presentation layer.
 *
 * @param {SpDialogProps} props - The sp-dialog props.
 * @param {(event: string, ...args: unknown[]) => void} emit - The component emit.
 * @param {Object} refs
 * @param {import("vue").Ref<HTMLDialogElement | null>} refs.dialog
 * @param {import("vue").Ref<HTMLElement | null>} refs.content
 * @returns {{ state: import("vue").Ref<string> }}
 */
export function useDialog(props, emit, { dialog, content }) {
  const isOpen = ref(toBoolean(props.open));
  const mounted = ref(false);

  const persistent = computed(() => toBoolean(props.persistent));
  const ignoreEscapeKey = computed(() => toBoolean(props.ignoreEscapeKey));
  const isHiddenTemporarily = computed(() => toBoolean(props.isHiddenTemporarily));

  const { state, transitionTo } = useDialogState();
  const { settle } = useDialogTransition(dialog);
  const focus = useDialogFocus(content);
  const scroll = useDialogScrollLock(props);

  /**
   * Whether the lifecycle may run yet. Open/close requests that arrive before
   * the component is mounted and the native <dialog> exists (an initial prop, a
   * prop change during setup) are ignored; onMounted drives the initial open.
   *
   * @returns {boolean}
   */
  function isReady() {
    return mounted.value && dialog.value != null;
  }

  // True from the moment the dialog opens until finalizeClose ends the session.
  // A temporary hide (the bottom-sheet swap) keeps it true, so the session spans
  // the hide. Single source of truth for "is a dialog session in progress".
  let sessionActive = false;

  /**
   * Open the dialog, unless it is temporarily hidden (another modal is shown
   * over it): while hidden it stays suppressed and REVEAL reopens it once
   * unhidden. Otherwise enter the opening state, start the session, promote the
   * native <dialog> to the top layer, lock scroll when applicable, then conclude
   * once the enter animation has settled.
   *
   * @returns {Promise<void>}
   */
  async function open() {
    if (isHiddenTemporarily.value) {
      return;
    }
    // Guardrail: only open from a closed or still-closing state.
    if (!transitionTo(State.OPENING)) {
      return;
    }
    // Start the session and capture the opener once. A temporary hide reopens
    // with the session already active, so the original opener is preserved.
    if (!sessionActive) {
      sessionActive = true;
      focus.capture();
    }
    if (!dialog.value.open) {
      dialog.value.showModal();
    }
    // Focus moves in before the enter animation settles, so typing during the
    // animation reaches the content.
    focus.moveIn();
    scroll.lock();
    await settle(concludeOpen);
  }

  /**
   * Conclude a settled open: enter the open state and move focus in again,
   * for a focus target that rendered after the open.
   */
  function concludeOpen() {
    transitionTo(State.OPEN);
    focus.moveIn();
  }

  /**
   * Close the dialog: enter the closing state, then conclude once the leave
   * animation has settled.
   *
   * @returns {Promise<void>}
   */
  async function close() {
    // Already settled closed (a temporary hide closed it visually while the
    // session stayed alive). A genuine close still ends the session, but a
    // transition out of CLOSED is rejected, so finalize directly.
    if (state.value === State.CLOSED) {
      if (!isTemporaryHide()) {
        finalizeClose();
      }
      return;
    }
    // Guardrail: only close from an opening or open state.
    if (!transitionTo(State.CLOSING)) {
      return;
    }
    await settle(concludeClose);
  }

  /**
   * Whether this close is a temporary hide (the bottom-sheet swap): the host
   * still wants the dialog open but momentarily out of the way behind another
   * modal, so the session must persist. A close where the dialog is already
   * closed-intent (isOpen false) is genuine and must finalize even if
   * isHiddenTemporarily is set (the hidden flag can flip true while a real
   * close is still animating).
   *
   * @returns {boolean}
   */
  function isTemporaryHide() {
    return isOpen.value && isHiddenTemporarily.value;
  }

  /**
   * Conclude a settled close: enter the closed state and close the native
   * dialog, then end the session unless this was a temporary hide. The state is
   * read here, at settle time, so a reveal or a real close that lands mid-close
   * is classified correctly.
   */
  function concludeClose() {
    transitionTo(State.CLOSED);
    // Native dialog.close() also restores focus when focus remains inside.
    if (toBoolean(props.noRestoreFocus) && !isTemporaryHide()) {
      focus.release();
    }
    dialog.value?.close();
    if (!isTemporaryHide()) {
      finalizeClose();
    }
  }

  /**
   * End the open session: clear the session flag, release the scroll lock,
   * restore focus to the opener, sync the open prop, and emit close. A no-op
   * without an active session, and leaving state untouched is correct: the
   * scroll lock and focus are acquired together with the session in open(), so
   * no session means nothing is locked, focused, or open to clean up. Deferred
   * while temporarily hidden (the bottom-sheet swap) so the page stays locked
   * and focus preserved until the dialog genuinely closes; lock()/unlock() are
   * idempotent per instance, so the reopen after an unhide re-locks without
   * double-counting.
   */
  function finalizeClose() {
    if (!sessionActive) {
      return;
    }
    sessionActive = false;
    scroll.unlock();
    focus.restore(toBoolean(props.noRestoreFocus));
    isOpen.value = false;
    emit("close");
  }

  /**
   * Whether a user dismissal (Escape, outside click) may close the dialog right
   * now: only while settled open and not persistent.
   *
   * @returns {boolean}
   */
  function isDismissible() {
    return isOpen.value && !persistent.value && state.value === State.OPEN;
  }

  /**
   * Close in response to a user dismissal (Escape, outside click) when allowed.
   */
  function dismiss() {
    if (isDismissible()) {
      close();
    }
  }

  /**
   * Reveal the dialog after a temporary hide: reopen it if it is still
   * open-intent. A close that arrived while hidden has already ended the session
   * (close() finalizes directly from the closed state), so there is nothing to
   * finalize here.
   */
  function reveal() {
    if (isOpen.value) {
      open();
    }
  }

  /**
   * The single entry point into the lifecycle. Handlers report a trigger and
   * this engine maps it to one action; the action functions hold any guards.
   * A no-op until ready.
   *
   * @param {string} trigger - One of the `Trigger` values.
   */
  function dispatch(trigger) {
    if (!isReady()) {
      return;
    }
    switch (trigger) {
      case Trigger.OPEN:
        open();
        break;
      case Trigger.CLOSE:
        close();
        break;
      case Trigger.DISMISS:
        dismiss();
        break;
      case Trigger.REVEAL:
        reveal();
        break;
    }
  }

  /**
   * Handle the native `cancel` event (Escape on a modal <dialog>).
   * preventDefault stops the browser's instant native close so the close
   * animation can run via the lifecycle; ignore-escape-key opts out of Escape
   * dismissal (the persistent case is handled by isDismissible).
   *
   * @param {Event} event - The dialog `cancel` event.
   */
  function handleCancel(event) {
    event.preventDefault();
    if (!ignoreEscapeKey.value) {
      dispatch(Trigger.DISMISS);
    }
  }

  /**
   * Stop Escape from reaching background handlers (tooltips, menus) when the
   * dialog blocks Escape dismissal. The native `cancel` path blocks the close
   * but not the keydown's propagation, so guard it here.
   *
   * @param {KeyboardEvent} event - The dialog Escape `keydown` event.
   */
  function handleEscapeKeydown(event) {
    if (persistent.value || ignoreEscapeKey.value) {
      event.stopPropagation();
    }
  }

  // A pointer press outside the content requests a dismissal.
  function handleClickOutside() {
    dispatch(Trigger.DISMISS);
  }

  /**
   * React to the internal open ref changing: dispatch the matching trigger and
   * announce the change for v-model via update-open.
   *
   * @param {boolean} value - The new open state.
   */
  function handleOpenChange(value) {
    dispatch(value ? Trigger.OPEN : Trigger.CLOSE);
    emit("update-open", value);
  }

  /**
   * Mirror the external open prop into the internal open ref.
   *
   * @param {boolean|string} value - The open prop (attribute strings included).
   */
  function handlePropOpenChange(value) {
    isOpen.value = toBoolean(value);
  }

  /**
   * React to the isHiddenTemporarily prop (the bottom-sheet swap). Hiding is just
   * a CLOSE: because the dialog is still open-intent (isOpen true), concludeClose
   * treats it as a temporary hide and keeps the session alive, so the dialog
   * closes visually but the scroll lock and focus persist. REVEAL restores it
   * once unhidden.
   *
   * @param {boolean|string} value - The isHiddenTemporarily prop.
   */
  function handlePropTemporaryHiddenChange(value) {
    dispatch(toBoolean(value) ? Trigger.CLOSE : Trigger.REVEAL);
  }

  /**
   * Mark the component ready and drive the initial open. The open watcher does
   * not fire for an already-true initial value, so the first open is dispatched
   * here once the dialog element exists.
   */
  function handleMounted() {
    mounted.value = true;
    if (isOpen.value) {
      dispatch(Trigger.OPEN);
    }
  }

  useEventListener(dialog, "cancel", handleCancel);
  onKeyStroke("Escape", handleEscapeKeydown, { target: dialog });
  onClickOutside(content, handleClickOutside);
  watch(isOpen, handleOpenChange);
  watch(() => props.open, handlePropOpenChange);
  watch(() => props.isHiddenTemporarily, handlePropTemporaryHiddenChange);
  onMounted(handleMounted);

  return { state };
}
